import * as React from 'react';
import i18nService, { LanguagesDefinition, SupportedLanguages, tl } from 'common/services/i18nService';
import { Icon } from 'semantic-ui-react';
import { ContextState } from 'author/src/store/context/reducer';
import { CustomPermissions } from 'author/src/store/context/types';
import { SessionState } from 'common/store/session/reducer';
import newAuthorUIService from 'author/src/services/newAuthorUIService';
import { openQuestionSetSummary, openReviewTool } from 'author/src/components/header/menuService';
import store from 'author/src/store';
import ReviewIcon from 'author/src/icons/ReviewIcon';
import VersionHistoryIcon from 'author/src/icons/VersionHistoryIcon';
import RightChevronIcon from 'author/src/icons/RightChevronIcon';
import SaveAsTemplateIcon from 'author/src/icons/SaveAsTemplateIcon';
import EditIcon from 'author/src/icons/EditIcon';
import PrintManagerIcon from 'author/src/icons/PrintManagerIcon';
import LogFile from 'author/src/icons/LogFile';
import OpenInNewTab from 'author/src/icons/OpenInNewTab';
import ShareWithGrpIcon from 'author/src/icons/ShareWithGrpIcon';
import ShareWithUserIcon from 'author/src/icons/ShareWithUserIcon';
import QuestionSetSummaryIcon from 'author/src/icons/QuestionSetSummaryIcon';
import { getQuestionSetSummaryConfig, shouldShowQuestionSetSummaryMenu } from 'author/src/features/questionSetSummary/config';
import AccessibleVerticalMenu from './AccessibleVerticalMenu';

import * as customMenuStyles from './CommonSubHeader.scss';
import * as styles from './Header.scss';
import {
    isQuestion,
    isQuestionMode,
    isQuestionReview,
    isQuestionSet,
    isSection,
} from '../../util/shared/sharedUtils';
import {
    hasAccessToEventLogsForUser,
    hasAuthorAccessViaCUR,
    hasCURAccessForAction,
    hasPrintManagerAccessViaCUR,
} from '../../services/permissions/commonPermissionService';
import { FeaturePrivilegesWithCurs, FeatureTypesWithCurs } from '../../services/permissions/typings';
import {
    ContentTypes,
    ContentTypesTemplates,
    VALID_ITEM_BANK_CONTENT_PRIVACY_MP_PROP_VALUES,
} from '../../store/common/constants';
import MenuSection from './MenuSection';

interface CommonHeaderProps {
    onBackToOriginalTool?: () => void;
    setLanguage: (language: string) => void;
    session?: SessionState;
    hideProfile?: boolean;
    supportedUiLanguages?: string[];
    renameContext?: () => void;
    openPrintManager?: () => void;
    openEventLogs?: () => void;
    showVersionHistory?: () => void;
    showSaveAsTemplate?: () => void;
    context?: ContextState;
    setShowShareModal?: () => void;
    setShowShareModalType?: (type: string) => void;
}

class CommonSubHeader extends React.PureComponent<CommonHeaderProps> {
    excludedShareContentTypes = [
        'assets',
        'ASSET',
        'category_taxonomy',
        'content_preset_essay_toolbar',
        'content_preset_essay_special_chars',
        'content_preset_math_symbol_palette',
        'content_preset_content_template',
        'content_preset_content_style',
        'content_preset_special_character_palette',
        'grading_workspaces',
        'content_preset_pci_tool',
        'content_preset_pci_template',
        'content_preset_answer_key_configuration',
        'content_preset_answer_key_type',
        'content_preset_dynamic_rubrics',
        'content_rubric',
        'rubrics',
    ];

    isItemBankContext = () => {
        const { context } = this.props;
        const itemBankId = context?.itemBankId ?? context?.currentItemContext?.itemBankId;
        return itemBankId != null && itemBankId !== -1;
    };

    isShareContentSupported = () => {
        const currentItemContext = this.props.context?.currentItemContext;
        if (currentItemContext?.isTemplate) {
            return false;
        }

        const contentType = currentItemContext?.contentType;
        return !contentType || !this.excludedShareContentTypes.includes(contentType);
    };

    isPrivateItemBankContent = () => {
        if (!this.isItemBankContext()) {
            return false;
        }

        return this.isValidItemBankPrivacyValueMpProp();
    };

    isValidItemBankPrivacyValueMpProp = () => {
        const contentPrivacyMpValue = window.marketplaceProperties.ITEM_BANK_CONTENT_PRIVACY_DEFAULT;
        return VALID_ITEM_BANK_CONTENT_PRIVACY_MP_PROP_VALUES.includes(contentPrivacyMpValue);
    };

    getCurrentContextCreationUserId = () => {
        const { context } = this.props;
        return context?.currentItemContext?.creationUserId || context?.contentEditStatus?.liveRevision?.creationUserId;
    };

    hasBuiltInAuthorOrExtendedAccess = () => {
        const privileges = this.props.session?.user?.privileges || [];
        return privileges.includes('system_role_testauthor_rel')
            || privileges.includes('system_role_testextendedcontentadmin_rel');
    };

    hasWriteReshareContentPrivilege = () => {
        const { WRITE_RESHARE_CONTENT } = FeaturePrivilegesWithCurs[FeatureTypesWithCurs.WRITE_RESHARE_CONTENT];
        const contentType = this.props.context?.currentItemContext?.contentType;
        if (!contentType || !WRITE_RESHARE_CONTENT[contentType]) {
            return false;
        }

        return hasCURAccessForAction(FeatureTypesWithCurs.WRITE_RESHARE_CONTENT, WRITE_RESHARE_CONTENT, contentType);
    };

    hasOwnerShareContentPrivilege = () => {
        const { OWNER_SHARE_CONTENT } = FeaturePrivilegesWithCurs[FeatureTypesWithCurs.OWNER_SHARE_CONTENT];
        const contentType = this.props.context?.currentItemContext?.contentType;
        if (!contentType || !OWNER_SHARE_CONTENT[contentType]) {
            return false;
        }

        return hasCURAccessForAction(FeatureTypesWithCurs.OWNER_SHARE_CONTENT, OWNER_SHARE_CONTENT, contentType);
    };

    isSharedViaAuthorSystemRole = () => {
        const { context, session } = this.props;
        const currentUserId = session?.user?.id;
        if (currentUserId == null) {
            return false;
        }

        const customPermissions = context?.currentItemContext?.customPermissions || [];
        return customPermissions.some(
            (permission: CustomPermissions) => permission.customUserRoleId == null
                && permission.granteeIds?.includes(Number(currentUserId)),
        );
    };

    canShareInItemBankContext = () => {
        const { session } = this.props;
        if (!this.isPrivateItemBankContent()) {
            return true;
        }

        if (this.isSharedViaAuthorSystemRole()) {
            return true;
        }

        if (this.hasBuiltInAuthorOrExtendedAccess()) {
            return true;
        }

        const currentUserId = session?.user?.id;
        const creationUserId = this.getCurrentContextCreationUserId();
        if (currentUserId && creationUserId && Number(currentUserId) === Number(creationUserId)) {
            return this.hasOwnerShareContentPrivilege();
        }

        return this.hasWriteReshareContentPrivilege();
    };

    handleLanguageChange = (event: any) => {
        const { setLanguage } = this.props;
        i18nService.changeLanguage(event.value, (err: object) => {
            if (!err) {
                setLanguage(event.value);
            }
        });
    };

    getLanguageOptions = (language: LanguagesDefinition) => {
        const { supportedUiLanguages } = this.props;
        const appSupportedLanguages: SupportedLanguages = i18nService.getSupportedLanguages(supportedUiLanguages);
        return Object.keys(appSupportedLanguages).map((lang: LanguagesDefinition) => {
            return {
                onClick: this.handleLanguageChange,
                content: appSupportedLanguages[lang],
                active: lang === language,
                value: lang,
            };
        });
    };

    onEditProfile = () => {
        const { session } = this.props;
        const { user } = session;
        window.open(`/dashboard/admin/#my-account/${user.id}`, '_blank');
    };

    onBackDashboard = () => {
        window.open('/student#list');
    };

    reviewMenuOption = () => {
        const { READ_ITEM_REVIEW_METADATA } = FeaturePrivilegesWithCurs[FeatureTypesWithCurs.ITEM_REVIEW_METADATA];
        const hasAccessToMetadata = hasCURAccessForAction(FeatureTypesWithCurs.ITEM_REVIEW_METADATA, READ_ITEM_REVIEW_METADATA);

        const { ITEM_REVIEW_ADDITIONAL_RESOURCES } = FeaturePrivilegesWithCurs[FeatureTypesWithCurs.ITEM_REVIEW_ADDITIONAL_RESOURCES];
        const hasAccessToAdditionalResources = hasCURAccessForAction(
            FeatureTypesWithCurs.ITEM_REVIEW_ADDITIONAL_RESOURCES, ITEM_REVIEW_ADDITIONAL_RESOURCES
        );

        const { READ_ITEM_REVIEW_STATS } = FeaturePrivilegesWithCurs[FeatureTypesWithCurs.ITEM_REVIEW_STATES];
        const hasAccessToItemStats = hasCURAccessForAction(FeatureTypesWithCurs.ITEM_REVIEW_STATES, READ_ITEM_REVIEW_STATS);

        const { READ_ATC_CONTENT_BLOCKS_USAGE_LISTS } = FeaturePrivilegesWithCurs[FeatureTypesWithCurs.ATC_CONTENT_BLOCKS_USAGE_LISTS];
        const hasAccessToContentBlocksUsageList = hasCURAccessForAction(
            FeatureTypesWithCurs.ATC_CONTENT_BLOCKS_USAGE_LISTS, READ_ATC_CONTENT_BLOCKS_USAGE_LISTS
        );

        const { READ_ANNOTATE } = FeaturePrivilegesWithCurs[FeatureTypesWithCurs.ANNOTATE_CONTENT];
        const hasAccessToReadAnnotate = hasCURAccessForAction(FeatureTypesWithCurs.ANNOTATE_CONTENT, READ_ANNOTATE);

        // IB-21322: view + list access on the current content type is enough to reach the Review tool.
        // Restricted to CUR-governed users: PREVIEW_CONTENT/CONTENT_LIST are not ExtendedAccessFeatures,
        // so hasCURAccessForAction returns true for everyone else and would bypass the review-tool check.
        const { READ_CONTENT_LIST } = FeaturePrivilegesWithCurs[FeatureTypesWithCurs.CONTENT_LIST];
        const isCurGovernedUser = Boolean(window.marketplaceProperties?.ENABLE_ORG_UNIT_WITH_CUR_ON_ITEM_BANK)
            && hasAuthorAccessViaCUR();
        const hasAccessToViewAndListContent = isCurGovernedUser
            && hasCURAccessForAction(FeatureTypesWithCurs.PREVIEW_CONTENT)
            && hasCURAccessForAction(FeatureTypesWithCurs.CONTENT_LIST, READ_CONTENT_LIST);

        const shouldShowReview = (
            !this.props.context.currentItemContext.isTemplate && (
                hasAccessToMetadata
                || hasAccessToAdditionalResources
                || hasAccessToItemStats
                || hasAccessToContentBlocksUsageList
                || hasAccessToReadAnnotate
                || hasAccessToViewAndListContent
            )
        );
        return shouldShowReview ? {
            optionProps: {
                onClick: () => openReviewTool(this.props.context),
            },
            content: (
                <div className={styles.subMenuItem}>
                    <ReviewIcon />
                    <span className={styles.subMenuItemTextAndIconReview}>Review</span>
                    <OpenInNewTab />
                </div>
            ),
        } : null;
    };

    renameMenuOption = () => {
        const isAutoGeneratedSection = this.props.context?.currentItemContext?.itemType === 'SECTION'
            && this.props.context?.currentItemContext?.contentBlockIdGenerationType === 'AUTOMATIC';

        const { CONTENT_BLOCK_EDIT } = FeaturePrivilegesWithCurs[FeatureTypesWithCurs.CONTENT_BLOCK_EDIT];
        const hasAccessToEditContent = hasCURAccessForAction(FeatureTypesWithCurs.CONTENT_BLOCK_EDIT, CONTENT_BLOCK_EDIT);

        const shouldShowRename = !this.props.context.isLocked
            && !isAutoGeneratedSection
            && hasAccessToEditContent;

        return shouldShowRename ? {
            optionProps: {
                onClick: this.props.renameContext,
                disabled: this.props.context.isLocked,
            },
            content: (
                <div
                    className={styles.subMenuItem}
                    aria-label={
                        this.props.context.isLocked ? tl('author.itemRenameModal.renameNotAvailable') : ''
                    }
                >
                    <EditIcon />
                    <span className={styles.subMenuItemText}>
                        Rename&nbsp;
                        {newAuthorUIService.contextName()}
                    </span>
                </div>
            ),
        } : null;
    };

    printManagerMenuOption = () => {
        const shouldShowPrintManager = (!isQuestionMode()
            || window.marketplaceProperties.ENABLE_PRINT_MANAGER_AT_QUESTION_LEVEL)
            && hasPrintManagerAccessViaCUR();

        return shouldShowPrintManager ? {
            optionProps: {
                onClick: this.props.openPrintManager,
            },
            content: (
                <div className={styles.subMenuItem}>
                    <PrintManagerIcon />
                    <span className={styles.subMenuItemText}>Print Manager</span>
                </div>
            ),
        } : null;
    };

    eventLogsMenuOption = () => (hasAccessToEventLogsForUser() ? {
        optionProps: {
            onClick: this.props.openEventLogs,
        },
        content: (
            <div className={styles.subMenuItem}>
                <LogFile />
                <span className={styles.subMenuItemTextAndIconLogs}>Event Logs</span>
                <OpenInNewTab />
            </div>
        ),
    } : null);

    questionSetSummaryMenuOption = () => {
        const questionSetSummaryConfig = getQuestionSetSummaryConfig();
        const shouldShowQuestionSetSummary = shouldShowQuestionSetSummaryMenu(isQuestionSet(), questionSetSummaryConfig);

        if (!shouldShowQuestionSetSummary) {
            return null;
        }

        return {
            optionProps: {
                onClick: () => openQuestionSetSummary(store, this.props.context),
            },
            content: (
                <div className={styles.subMenuItem}>
                    <QuestionSetSummaryIcon />
                    <span className={styles.subMenuItemText}>Question Set Summary</span>
                </div>
            ),
        };
    };

    versionHistoryMenuOption = () => {
        const enableAuthorContentVersion = window.marketplaceProperties.ENABLE_AUTHOR_CONTENT_MAJOR_VERSIONS;
        const { READ_CONTENT_BLOCK_SHOW_VERSION_HISTORY } = FeaturePrivilegesWithCurs[FeatureTypesWithCurs.CONTENT_BLOCK_VERSION_HISTORY];
        const hasAccessToContentBlockVersionHistory = hasCURAccessForAction(
            FeatureTypesWithCurs.CONTENT_BLOCK_VERSION_HISTORY,
            READ_CONTENT_BLOCK_SHOW_VERSION_HISTORY,
        );
        const { READ_INTERACTION_BLOCK_SHOW_VERSION_HISTORY } = FeaturePrivilegesWithCurs[FeatureTypesWithCurs.QUESTION_VERSION_HISTORY];
        const hasAccessToQuestionVersionHistory = hasCURAccessForAction(
            FeatureTypesWithCurs.QUESTION_VERSION_HISTORY,
            READ_INTERACTION_BLOCK_SHOW_VERSION_HISTORY,
        );

        const hasVersionHistoryAccess = isQuestion() || isQuestionReview()
            ? hasAccessToQuestionVersionHistory
            : hasAccessToContentBlockVersionHistory;

        const shouldShowVersionHistory = !this.props.context.currentItemContext.isTemplate
            && enableAuthorContentVersion
            && hasVersionHistoryAccess;

        return shouldShowVersionHistory ? {
            optionProps: {
                onClick: this.props.showVersionHistory,
            },
            content: (
                <div className={styles.subMenuItem}>
                    <VersionHistoryIcon />
                    <span className={styles.subMenuItemTextAndIconTemplate}>Show version history</span>
                    <RightChevronIcon />
                </div>
            ),
        } : null;
    };

    saveAsTemplateMenuOption = () => {
        const enableContentItemTemplates = window.marketplaceProperties.ENABLE_CONTENT_ITEM_TEMPLATES;
        const enableQuestionTemplates = window.marketplaceProperties.ENABLE_QUESTION_TEMPLATES;
        const enableQuestionSetTemplates = window.marketplaceProperties.ENABLE_QUESTION_SET_TEMPLATES;

        const { CONTENT_BLOCK_EDIT } = FeaturePrivilegesWithCurs[FeatureTypesWithCurs.CONTENT_BLOCK_EDIT];
        const hasAccessToSaveAsTemplateViaCur = hasCURAccessForAction(
            FeatureTypesWithCurs.CONTENT_BLOCK_EDIT,
            CONTENT_BLOCK_EDIT,
            ContentTypesTemplates[ContentTypes.SECTION]
        );

        const shouldShowSaveAsTemplate = !this.props.context.currentItemContext.isTemplate
            && (
                (isSection() && enableContentItemTemplates && hasAccessToSaveAsTemplateViaCur)
                || ((isQuestion() || isQuestionReview()) && enableQuestionTemplates)
                || (isQuestionSet() && enableQuestionSetTemplates)
            );

        return shouldShowSaveAsTemplate ? {
            optionProps: {
                onClick: this.props.showSaveAsTemplate,
            },
            content: (
                <div className={styles.subMenuItem}>
                    <SaveAsTemplateIcon />
                    <span className={styles.subMenuItemText}>Save as template</span>
                </div>
            ),
        } : null;
    };

    isContentPrivacyPublic = () => {
        const { context } = this.props;
        const itemBankId = context?.itemBankId ?? context?.currentItemContext?.itemBankId;
        const isBanked = itemBankId != null && itemBankId !== -1 && itemBankId > 0;
        return isBanked && !!(context?.currentItemContext?.securityInherit ?? context?.assessmentData?.securityInherit);
    };

    shareWithUsersMenuOption = () => {
        if (!newAuthorUIService.isNewAuthorUIEnabled() || !this.isShareContentSupported() || !this.canShareInItemBankContext()) {
            return null;
        }

        if (this.isContentPrivacyPublic()) {
            return null;
        }

        return {
            optionProps: {
                onClick: () => {
                    if (this.props.setShowShareModal) {
                        this.props.setShowShareModal();
                    }
                    if (this.props.setShowShareModalType) {
                        this.props.setShowShareModalType('USER');
                    }
                },
            },
            content: (
                <div className={styles.subMenuItem}>
                    <ShareWithUserIcon />
                    <span className={styles.subMenuItemText}>Share with users</span>
                </div>
            ),
        };
    }

    shareWithOrgUnitMenuOption = () => {
        if (!newAuthorUIService.isNewAuthorUIEnabled() || !this.isShareContentSupported() || !this.canShareInItemBankContext()) {
            return null;
        }

        if (this.isContentPrivacyPublic()) {
            return null;
        }

        return {
            optionProps: {
                onClick: () => {
                    if (this.props.setShowShareModal) {
                        this.props.setShowShareModal();
                    }
                    if (this.props.setShowShareModalType) {
                        this.props.setShowShareModalType('ORG-UNIT');
                    }
                },
            },
            content: (
                <div className={styles.subMenuItem}>
                    <ShareWithGrpIcon />
                    <span className={styles.subMenuItemText}>Share with access group</span>
                </div>
            ),
        };
    }

    newAuthorUIMenuOptions = () => {
        if (isQuestionSet()) {
            return [
                this.questionSetSummaryMenuOption(),
                this.renameMenuOption(),
                this.shareWithUsersMenuOption(),
                this.shareWithOrgUnitMenuOption(),
                this.printManagerMenuOption(),
                this.eventLogsMenuOption(),
                this.saveAsTemplateMenuOption(),
            ];
        }

        if (isSection()) {
            return [
                this.reviewMenuOption(),
                this.renameMenuOption(),
                this.printManagerMenuOption(),
                this.eventLogsMenuOption(),
                this.versionHistoryMenuOption(),
                this.saveAsTemplateMenuOption(),
            ];
        }

        if (isQuestion()) {
            return [
                this.reviewMenuOption(),
                this.eventLogsMenuOption(),
                this.printManagerMenuOption(),
                this.versionHistoryMenuOption(),
                this.renameMenuOption(),
                this.shareWithUsersMenuOption(),
                this.shareWithOrgUnitMenuOption(),
                this.saveAsTemplateMenuOption(),
            ];
        }

        if (isQuestionReview()) {
            return [
                this.reviewMenuOption(),
                this.eventLogsMenuOption(),
                this.printManagerMenuOption(),
                this.versionHistoryMenuOption(),
                this.renameMenuOption(),
                this.shareWithUsersMenuOption(),
                this.shareWithOrgUnitMenuOption(),
                this.saveAsTemplateMenuOption(),
            ];
        }

        return [];
    };

    render() {
        const { session, hideProfile } = this.props;
        const { language, user } = session;
        const {
            firstName, lastName, userName, email, isStudent,
        } = user;

        const options = newAuthorUIService.isNewAuthorUIEnabled()
            ? this.newAuthorUIMenuOptions().filter((m) => m !== null)
            : [
                {
                    optionProps: {
                        className: styles.removeBotPadding,
                        disabled: true,
                    },
                    content: (
                        <MenuSection
                            boldTitle={`${firstName || ''} ${lastName || ''}`}
                            subtitle={userName}
                            text={email}
                        />
                    ),
                },
                {
                    optionProps: {
                        onClick: this.onEditProfile,
                        hide: isStudent || hideProfile,
                    },
                    content: (
                        <React.Fragment>
                            {tl('common.headerRight.options.profile')}
                            <Icon name="user" />
                        </React.Fragment>
                    ),
                },
                {
                    optionProps: {
                        onClick: this.onBackDashboard,
                        hide: !isStudent,
                    },
                    content: (
                        <React.Fragment>
                            {tl('grading.header.backToDashboard')}
                            <Icon name="home" />
                        </React.Fragment>
                    ),
                },
                {
                    optionProps: {
                        hasNoItemDivider: true,
                    },
                    content: (tl('common.header.selectLanguage')),
                    subOptions: this.getLanguageOptions(language),
                },
            ];

        return <AccessibleVerticalMenu className={customMenuStyles.subHeader} OptionList={options} />;
    }
}

export default CommonSubHeader;
